import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { RETAIL_MODE } from '@/lib/retailMode';
import { PlatformContactSheet } from '@/components/contact/PlatformContactSheet';
import { useAuth } from '@/hooks/useAuth';
import { useIsMobile } from '@/hooks/use-mobile';




export const Footer = () => {
	const location = useLocation();
	const { user } = useAuth();
	const isMobile = useIsMobile();
	const [isVisible, setIsVisible] = useState(false);
	const [isAtBottom, setIsAtBottom] = useState(false);
	const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1280);
	const [contactOpen, setContactOpen] = useState(false);
	const lastScrollY = useRef(0);
	const scrollThreshold = useRef(0);

	// Static pages where footer should always be visible
	const staticPages = ['/privacy', '/terms', '/disclaimer', '/contact', '/admin', '/auth'];
	const isStaticPage = staticPages.includes(location.pathname);
	// Landing page has its own full footer — skip the floating one entirely
	// In retail mode, / is the storefront, so keep the floating footer.
	const isLandingPage = location.pathname === '/' && !RETAIL_MODE;
	// Item detail pages own the bottom strip on mobile (fixed action bar).
	// Two fixed bars in the same band caused the "double footer" bleed-through.
	const isMobileItemPage = isMobile && /^\/(item\/|s\/[^/]+\/item\/)/.test(location.pathname);

	// Resolve the active scroll source: prefer the in-app inner scroller,
	// fall back to whichever element actually scrolls the page (body or
	// documentElement — depends on overflow rules; in this app `html` is
	// `overflow:hidden` so body is the real scroller on PageShell scroll="page"
	// routes like /menus, /menu-editor, /menu/:slug).
	const isScrollSourceVisible = (el: Element | null): el is Element => {
		if (!el) return false;
		return getComputedStyle(el).display !== 'none' && (el as HTMLElement).clientHeight > 0;
	};

	const resolveScrollSource = (): {
		target: EventTarget;
		getScrollTop: () => number;
		getClientHeight: () => number;
		getScrollHeight: () => number;
	} | null => {
		const isMobile = window.innerWidth < 1024;
		let el: Element | null = null;
		if (isMobile) el = document.querySelector('.main-content-scroll-mobile');
		else el = document.querySelector('.main-content-scroll-desktop');
		if (!isScrollSourceVisible(el)) el = document.querySelector('.main-content-scroll');
		if (!isScrollSourceVisible(el)) el = null;

		if (el) {
			return {
				target: el,
				getScrollTop: () => (el as Element).scrollTop,
				getClientHeight: () => (el as Element).clientHeight,
				getScrollHeight: () => (el as Element).scrollHeight,
			};
		}

		// Pick the real page-level scroller. Body wins when it's the
		// overflow:auto element; otherwise fall back to documentElement/window.
		const body = document.body;
		const docEl = document.documentElement;
		const bodyOverflowY = getComputedStyle(body).overflowY;
		const bodyScrolls = (bodyOverflowY === 'auto' || bodyOverflowY === 'scroll') && body.scrollHeight > body.clientHeight;
		if (bodyScrolls) {
			return {
				target: body,
				getScrollTop: () => body.scrollTop,
				getClientHeight: () => body.clientHeight,
				getScrollHeight: () => body.scrollHeight,
			};
		}
		return {
			target: window,
			getScrollTop: () => window.scrollY || docEl.scrollTop || 0,
			getClientHeight: () => window.innerHeight,
			getScrollHeight: () => docEl.scrollHeight,
		};
	};

	// Check if content fits in viewport (no scrolling needed)
	const checkContentHeight = () => {
		const src = resolveScrollSource();
		if (!src) {
			setIsAtBottom(true);
			return;
		}
		const ch = src.getClientHeight();
		const sh = src.getScrollHeight();
		const contentFitsInViewport = sh <= ch + 50;
		if (contentFitsInViewport) {
			setIsAtBottom(true);
		} else {
			const atBottom = src.getScrollTop() + ch >= sh - 50;
			setIsAtBottom(atBottom);
		}
	};


	useEffect(() => {
		// Reset state on route change
		setIsVisible(false);
		setIsAtBottom(false);
		lastScrollY.current = 0;
		scrollThreshold.current = 0;

		const JITTER_PX = 2; // ignore micro scroll jitter (trackpad/momentum)
		const SHOW_THRESHOLD = 10; // upward distance needed to reveal footer
		const DOWN_HIDE_THRESHOLD = 6; // require meaningful downward scroll to hide
		const TOP_HIDE_ZONE = 8; // only force-hide when truly back at the top

		let activeSource: ReturnType<typeof resolveScrollSource> = null;

		// Measure the element the scroll event actually fired on — the
		// pre-resolved activeSource can point at the wrong element on desktop
		// (e.g. window resolved while body is the real scroller), which made
		// scrollTop read as 0 and force-hid the footer permanently.
		const metricsFromEvent = (e: Event) => {
			const t = e.target;
			if (t && t !== document && t !== window && t instanceof Element) {
				const el = t as HTMLElement;
				if (el.scrollHeight > el.clientHeight) {
					return { top: el.scrollTop, ch: el.clientHeight, sh: el.scrollHeight };
				}
			}
			// window/document scroll (or non-scrollable target): use whichever
			// page-level metric is actually non-zero.
			const docEl = document.documentElement;
			const top = Math.max(window.scrollY || 0, docEl.scrollTop || 0, document.body.scrollTop || 0);
			const sh = Math.max(docEl.scrollHeight, document.body.scrollHeight);
			return { top, ch: window.innerHeight, sh };
		};

		const handleScroll = (e: Event) => {
			if (!activeSource) return;
			const { top: currentScrollY, ch, sh } = metricsFromEvent(e);
			const delta = currentScrollY - lastScrollY.current;

			const atBottom = currentScrollY + ch >= sh - 50;
			setIsAtBottom(atBottom);

			// Truly at the top of the page AND not actively scrolling up: keep
			// footer hidden so it doesn't linger after a scroll-to-top gesture.
			// An upward gesture (delta < 0) at the top still reveals — otherwise
			// users near the top of short pages would never see the footer.
			if (currentScrollY <= TOP_HIDE_ZONE && delta >= 0) {
				if (Math.abs(delta) >= JITTER_PX) {
					setIsVisible(false);
					scrollThreshold.current = 0;
					lastScrollY.current = currentScrollY;
				}
				return;
			}


			// Ignore micro jitter — don't update lastScrollY so accumulation stays accurate
			if (Math.abs(delta) < JITTER_PX) return;

			if (delta < 0) {
				scrollThreshold.current += -delta;
				if (scrollThreshold.current >= SHOW_THRESHOLD) {
					setIsVisible(true);
				}
			} else if (delta >= DOWN_HIDE_THRESHOLD) {
				setIsVisible(false);
				scrollThreshold.current = 0;
			}

			lastScrollY.current = currentScrollY;
		};

		// Track which targets we've attached to so we can rebind if the
		// scroll source changes (e.g. content loads in and body becomes
		// scrollable after the initial 100ms resolve).
		const attached = new Set<EventTarget>();
		const attach = (t: EventTarget) => {
			if (attached.has(t)) return;
			t.addEventListener('scroll', handleScroll, { passive: true });
			attached.add(t);
		};
		const rebindSource = () => {
			activeSource = resolveScrollSource();
			if (!activeSource) return;
			attach(activeSource.target);
			// When falling back to body/window, attach both so we catch the
			// real scroll event regardless of which element ends up scrolling.
			const isInnerScroller = activeSource.target instanceof Element
				&& (activeSource.target as Element).classList.contains('main-content-scroll');
			if (!isInnerScroller) {
				attach(document.body);
				attach(window);
			}
		};

		// Small delay to ensure DOM is ready after route change
		const timeoutId = setTimeout(() => {
			rebindSource();
			checkContentHeight();
		}, 100);

		// Re-check content height (and source) after async content loads
		const recheckTimeoutId = setTimeout(() => {
			rebindSource();
			checkContentHeight();
		}, 500);

		// Additional recheck for slower loading pages (like storefronts with images)
		const lateRecheckTimeoutId = setTimeout(() => {
			rebindSource();
			checkContentHeight();
		}, 1500);

		return () => {
			clearTimeout(timeoutId);
			clearTimeout(recheckTimeoutId);
			clearTimeout(lateRecheckTimeoutId);
			attached.forEach((t) => t.removeEventListener('scroll', handleScroll));
			attached.clear();
		};
	}, [location.pathname]);



	// Handle window resize for responsive clipPath and content height check
	useEffect(() => {
		const handleResize = () => {
			setWindowWidth(window.innerWidth);
			// Re-check content height on resize
			checkContentHeight();
		};

		// Debounced resize handler
		let resizeTimeout: ReturnType<typeof setTimeout>;
		const debouncedResize = () => {
			clearTimeout(resizeTimeout);
			resizeTimeout = setTimeout(handleResize, 150);
		};

		window.addEventListener('resize', debouncedResize);
		return () => {
			window.removeEventListener('resize', debouncedResize);
			clearTimeout(resizeTimeout);
		};
	}, []);

	// Override visibility for static pages or when at bottom.
	// On the retail storefront root, ignore isAtBottom so the footer stays
	// hidden until the user actively scrolls up (matches the "slides in on
	// scroll-up, hides on scroll-down" behavior the user expects).
	const isRetailHome = RETAIL_MODE && location.pathname === '/';
	// On mobile with BottomNav present (signed in), keep the footer hidden by
	// default so it doesn't stack under the floating nav. Reveal only on an
	// active scroll-up gesture. Static legal pages still force-show.
	const scrollRevealOnly = !isStaticPage && (isRetailHome || (isMobile && !!user));
	const shouldBeVisible = isStaticPage || isVisible || (isAtBottom && !scrollRevealOnly);

	if (isLandingPage || isMobileItemPage) return null;

	return (
		<>
			<footer
				className={`fixed bottom-0 sm:left-1/2 sm:-translate-x-1/2 left-0 right-0 z-[10] h-[36px] w-full sm:w-auto px-6 lg:px-10 bg-background/80 backdrop-blur-md shadow-lg shadow-black/20 transition-transform duration-normal ease-standard sm:rounded-none ${
					shouldBeVisible ? 'translate-y-0' : 'translate-y-full'
				}`}
				style={{
					clipPath: windowWidth >= 640 ? 'polygon(6% 0%, 94% 0%, 100% 100%, 0% 100%)' : 'none'
				}}
			>
				<div className="h-full flex items-center justify-center gap-[3vw] sm:gap-8 text-xs whitespace-nowrap">
					<span className="text-white/65 py-0.5">© 2026 CardSimp</span>
					<Link to="/safety" className="text-white/40 hover:text-active-gold py-0.5">Shipping &amp; Returns</Link>
					<Link to="/terms" className="text-white/40 hover:text-active-gold py-0.5">Terms</Link>
					<Link to="/privacy" className="text-white/40 hover:text-active-gold py-0.5">Privacy</Link>
					{RETAIL_MODE ? (
						<button
							type="button"
							onClick={() => setContactOpen(true)}
							className="text-white/40 hover:text-active-gold py-0.5 hidden sm:inline"
						>
							Contact
						</button>
					) : (
						<Link to="/contact" className="text-white/40 hover:text-active-gold py-0.5 hidden sm:inline">Contact</Link>
					)}
				</div>
			</footer>
			{RETAIL_MODE && (
				<PlatformContactSheet
					open={contactOpen}
					onOpenChange={setContactOpen}
				/>
			)}
		</>
	);
};
